import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Building, Bot, Users } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import useOrganizationStore from '@/stores/useOrganizationStore'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { MemoryList } from '@/components/organization/MemoryList'

const OrganizationDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const {
    currentOrganization: org,
    fetchOrganizationById,
    isLoading,
  } = useOrganizationStore()

  useEffect(() => {
    if (id) {
      fetchOrganizationById(id)
    }
  }, [id, fetchOrganizationById])

  if (isLoading || !org || org.id !== id) {
    return (
      <div className="container max-w-6xl mx-auto py-8 px-4 space-y-6">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="container max-w-6xl mx-auto py-8 px-4">
      <div className="flex items-center gap-4 mb-6">
        <Building className="h-10 w-10 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">{org.name}</h1>
          <p className="text-muted-foreground">ID: {org.id}</p>
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList className="mb-4">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="agents">Remote Agents</TabsTrigger>
          <TabsTrigger value="memories">Memories</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <strong className="font-semibold">Status:</strong>
                <Badge
                  variant={org.status === 'active' ? 'default' : 'outline'}
                >
                  {org.status}
                </Badge>
              </div>
              <div className="text-sm">
                <strong className="font-semibold">Default:</strong>{' '}
                {org.isDefault ? 'Yes' : 'No'}
              </div>
              <div className="text-sm">
                <strong className="font-semibold">Created At:</strong>{' '}
                {new Date(org.createdAt).toLocaleString()}
              </div>
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5" /> Members ({org.members.length})
                </h3>
                <div className="space-y-2">
                  {org.members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-2 bg-muted rounded-md"
                    >
                      <span>User ID: {member.id}</span>
                      <Badge variant="secondary">{member.role}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" /> Remote Agents (
                {org.remoteAgents.length})
              </CardTitle>
              <CardDescription>
                Agents configured for this organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {org.remoteAgents.length > 0 ? (
                <div className="space-y-2">
                  {org.remoteAgents.map((agent) => (
                    <div
                      key={agent.id}
                      className="p-3 border rounded-md flex justify-between items-center"
                    >
                      <p className="font-semibold">{agent.name}</p>
                      <Badge
                        className={
                          agent.status === 'online'
                            ? 'bg-green-500 text-white'
                            : ''
                        }
                      >
                        {agent.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No remote agents found for this organization.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="memories">
          <MemoryList organizationId={org.id} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default OrganizationDetailsPage
